"use client";

import { c as _c } from "react/compiler-runtime";
import WavesIcon from "@/components/common/icons/WavesIcon";
import { Spinner } from "@/components/dotLoader/DotLoader";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { ArrowTopRightOnSquareIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { getWaveHref } from "./userPageProfileWave.helpers";
import type { resolveWavePickerViewState } from "./userPageProfileWave.helpers";
type WavePickerVariant = "panel" | "dropdown" | "mobile-sheet";
type WavePickerState = ReturnType<typeof resolveWavePickerViewState>;
type ReadyWavePickerState = Extract<WavePickerState, {
  kind: "ready";
}>;
function CompactCandidateWaveRow(t0) {
  const $ = _c(22);
  const {
    wave,
    isSelected,
    isSubmitting,
    onSelect,
    isMobileSheet
  } = t0;
  let trailingIndicator = null;
  if (isSubmitting) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Spinner dimension={14} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    trailingIndicator = t1;
  } else {
    if (isSelected) {
      const t1 = `tw-flex-shrink-0 tw-text-emerald-400 ${isMobileSheet ? "tw-h-6 tw-w-6" : "tw-h-5 tw-w-5"}`;
      let t2;
      if ($[1] !== t1) {
        t2 = <CheckCircleIcon className={t1} />;
        $[1] = t1;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      trailingIndicator = t2;
    }
  }
  let t1;
  if ($[3] !== onSelect || $[4] !== wave.id) {
    t1 = () => onSelect(wave.id);
    $[3] = onSelect;
    $[4] = wave.id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = isSubmitting || isSelected;
  const t3 = `tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-x-3 tw-rounded-xl tw-border-0 tw-text-left tw-font-medium tw-text-white tw-transition tw-duration-200 tw-ease-out focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-inset focus-visible:tw-ring-primary-400 disabled:tw-cursor-default ${isMobileSheet ? "tw-px-4 tw-py-3" : "tw-px-3 tw-py-2.5"} ${isSelected ? "tw-bg-white/10" : "tw-bg-transparent desktop-hover:hover:tw-bg-white/5"}`;
  const t4 = `tw-mb-0 tw-truncate tw-font-semibold tw-tracking-tight ${isMobileSheet ? "tw-text-base" : "tw-text-sm"}`;
  let t5;
  if ($[6] !== t4 || $[7] !== wave.name) {
    t5 = <h3 className={t4}>{wave.name}</h3>;
    $[6] = t4;
    $[7] = wave.name;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `tw-mb-0 tw-truncate tw-text-iron-500 ${isMobileSheet ? "tw-text-sm" : "tw-text-xs"}`;
  let t7;
  if ($[9] !== t6 || $[10] !== wave.metrics.drops_count || $[11] !== wave.metrics.subscribers_count) {
    t7 = <p className={t6}>{wave.metrics.drops_count} posts • {wave.metrics.subscribers_count}{" "}joined</p>;
    $[9] = t6;
    $[10] = wave.metrics.drops_count;
    $[11] = wave.metrics.subscribers_count;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <div className="tw-min-w-0 tw-flex-1 tw-space-y-0.5">{t5}{t7}</div>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t8 || $[20] !== trailingIndicator) {
    t9 = <button type="button" onClick={t1} disabled={t2} className={t3}>{t8}{trailingIndicator}</button>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t8;
    $[20] = trailingIndicator;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function PanelCandidateWaveRow(t0) {
  const $ = _c(37);
  const {
    wave,
    isSelected,
    isSubmitting,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== wave.picture) {
    t1 = wave.picture ? getScaledImageUri(wave.picture, ImageScale.W_AUTO_H_50) : null;
    $[0] = wave.picture;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const imageSrc = t1;
  const t2 = `tw-transition tw-duration-300 tw-ease-out tw-relative tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-px-3 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between sm:tw-gap-4 sm:tw-px-4 sm:tw-py-4 ${isSelected ? "tw-bg-emerald-500/5" : "tw-bg-iron-950 desktop-hover:hover:tw-bg-iron-900"}`;
  let t3;
  if ($[2] !== isSelected) {
    t3 = isSelected && <span className="tw-absolute tw-left-3 tw-top-1/2 tw-h-9 tw-w-1 -tw-translate-y-1/2 tw-rounded-full tw-bg-emerald-400 tw-shadow-sm sm:tw-left-0" />;
    $[2] = isSelected;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== imageSrc || $[5] !== wave.name) {
    t4 = <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-iron-900 tw-ring-1 tw-ring-white/10 sm:tw-h-10 sm:tw-w-10">{imageSrc ? <Image src={imageSrc} alt={wave.name ? `Wave ${wave.name}` : "Wave picture"} width={40} height={40} className="tw-h-full tw-w-full tw-rounded-full tw-object-cover" /> : <WavesIcon className="tw-h-4 tw-w-4 tw-text-iron-300" />}</div>;
    $[4] = imageSrc;
    $[5] = wave.name;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== wave.name) {
    t5 = <h3 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100">{wave.name}</h3>;
    $[7] = wave.name;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== wave.metrics.drops_count || $[10] !== wave.metrics.subscribers_count) {
    t6 = <p className="tw-mb-0 tw-truncate tw-text-xs tw-text-iron-500">{wave.metrics.drops_count} posts • {wave.metrics.subscribers_count}{" "}joined</p>;
    $[9] = wave.metrics.drops_count;
    $[10] = wave.metrics.subscribers_count;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="tw-min-w-0 tw-space-y-1">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t7) {
    t8 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">{t4}{t7}</div>;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== wave) {
    t9 = getWaveHref(wave);
    $[18] = wave;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span>View</span>;
    t11 = <ArrowTopRightOnSquareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t9) {
    t12 = <Link href={t9} prefetch={false} className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-transparent tw-text-sm tw-font-medium tw-text-iron-400 tw-no-underline tw-transition tw-duration-300 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-iron-400 desktop-hover:hover:tw-text-iron-200 desktop-hover:hover:tw-no-underline">{t10}{t11}</Link>;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isSelected || $[25] !== isSubmitting || $[26] !== onSelect || $[27] !== wave.id) {
    t13 = isSelected ? <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-emerald-500/25 tw-bg-emerald-500/10 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-emerald-300"><CheckCircleIcon className="-tw-ml-1.5 tw-h-4 tw-w-4 tw-flex-shrink-0" />Active</span> : <button type="button" onClick={() => onSelect(wave.id)} disabled={isSubmitting} className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-white/5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-100 tw-transition tw-duration-300 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-iron-300 disabled:tw-cursor-not-allowed disabled:tw-border-white/5 disabled:tw-text-iron-500 desktop-hover:hover:tw-border-white/15 desktop-hover:hover:tw-bg-white/10 sm:tw-px-4 sm:tw-py-2 sm:tw-text-sm">{isSubmitting ? <Spinner dimension={14} /> : null}<span>{isSubmitting ? "Setting active" : "Set active"}</span></button>;
    $[24] = isSelected;
    $[25] = isSubmitting;
    $[26] = onSelect;
    $[27] = wave.id;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 sm:tw-justify-end">{t12}{t13}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t2 || $[34] !== t3 || $[35] !== t8) {
    t15 = <div className={t2}>{t3}{t8}{t14}</div>;
    $[32] = t14;
    $[33] = t2;
    $[34] = t3;
    $[35] = t8;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}
function CandidateWaveRow(t0) {
  const $ = _c(11);
  const {
    wave,
    isSelected,
    isSubmitting,
    onSelect,
    variant
  } = t0;
  if (variant === "panel") {
    let t1;
    if ($[0] !== isSelected || $[1] !== isSubmitting || $[2] !== onSelect || $[3] !== wave) {
      t1 = <PanelCandidateWaveRow wave={wave} isSelected={isSelected} isSubmitting={isSubmitting} onSelect={onSelect} />;
      $[0] = isSelected;
      $[1] = isSubmitting;
      $[2] = onSelect;
      $[3] = wave;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  const t1 = variant === "mobile-sheet";
  let t2;
  if ($[5] !== isSelected || $[6] !== isSubmitting || $[7] !== onSelect || $[8] !== t1 || $[9] !== wave) {
    t2 = <CompactCandidateWaveRow wave={wave} isSelected={isSelected} isSubmitting={isSubmitting} onSelect={onSelect} isMobileSheet={t1} />;
    $[5] = isSelected;
    $[6] = isSubmitting;
    $[7] = onSelect;
    $[8] = t1;
    $[9] = wave;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
export default function UserPageProfileWavePickerReady(t0) {
  const $ = _c(29);
  const {
    state,
    title,
    selectedWaveId,
    submittingWaveId,
    onSelectWave,
    variant
  } = t0;
  let t1;
  if ($[0] !== onSelectWave || $[1] !== selectedWaveId || $[2] !== submittingWaveId || $[3] !== variant) {
    t1 = candidateWave => <CandidateWaveRow key={candidateWave.id} wave={candidateWave} isSelected={selectedWaveId === candidateWave.id} isSubmitting={submittingWaveId === candidateWave.id} onSelect={onSelectWave} variant={variant} />;
    $[0] = onSelectWave;
    $[1] = selectedWaveId;
    $[2] = submittingWaveId;
    $[3] = variant;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const renderCandidateWaveRow = t1;
  if (variant === "dropdown") {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p className="tw-mb-0 tw-px-3 tw-pb-1 tw-pt-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Profile wave</p>;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== renderCandidateWaveRow || $[7] !== state.waves) {
      t3 = state.waves.map(renderCandidateWaveRow);
      $[6] = renderCandidateWaveRow;
      $[7] = state.waves;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t3) {
      t4 = <section className="tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-py-2 tw-shadow-2xl"><div className="tw-no-scrollbar tw-flex tw-max-h-96 tw-flex-col tw-gap-1 tw-overflow-y-auto tw-overflow-x-hidden tw-px-1.5">{t2}<div className="tw-flex tw-flex-col tw-gap-1">{t3}</div></div></section>;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (variant === "mobile-sheet") {
    let t2;
    if ($[11] !== renderCandidateWaveRow || $[12] !== state.waves) {
      t2 = state.waves.map(renderCandidateWaveRow);
      $[11] = renderCandidateWaveRow;
      $[12] = state.waves;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== t2) {
      t3 = <div className="tw-px-4 sm:tw-px-6"><div className="tw-flex tw-flex-col tw-gap-2">{t2}</div></div>;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    return t3;
  }
  let t2;
  if ($[16] !== title) {
    t2 = <h2 className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-100 md:tw-text-xl">{title}</h2>;
    $[16] = title;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-font-normal tw-leading-relaxed tw-text-iron-500 md:tw-mt-2">Choose the wave you want to use as your featured wave.</p>;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t2) {
    t4 = <div className="tw-max-w-2xl">{t2}{t3}</div>;
    $[19] = t2;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== renderCandidateWaveRow || $[22] !== state.waves) {
    t5 = state.waves.map(renderCandidateWaveRow);
    $[21] = renderCandidateWaveRow;
    $[22] = state.waves;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t5) {
    t6 = <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-gap-3">{t5}</div>;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t4 || $[27] !== t6) {
    t7 = <section className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/10 tw-bg-black sm:tw-rounded-3xl"><div className="tw-space-y-4 tw-p-3.5 sm:tw-space-y-6 sm:tw-p-5 md:tw-p-6">{t4}{t6}</div></section>;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}